import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useFormCepAutocomplete } from '@area-restrita/client';
import { CompanyDetailQueryResponse } from '@area-restrita/api-client/model/companyDetailQueryResponse';
import { useUpdateCompany } from '@area-restrita/api-client/endpoints/companies/companies.gen';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Form, FormField } from '@/components/ui/form';
import InlineInput from '@/components/InlineInput';
import { Button } from '@/components/ui/button';
import MaskedInput from '@/components/MaskedInput';
import UfSelector from '@/components/QuerySelectors/UfSelector';
import CitySelector from '@/components/QuerySelectors/CitySelector';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  fullCompanySchema,
  IFullCompanyFormSchema,
} from '../../components/companySchema';

interface IProps {
  company: CompanyDetailQueryResponse;
}

const CompanyOverview: React.FC<IProps> = ({ company }) => {
  const form = useForm<IFullCompanyFormSchema>({
    defaultValues: useMemo(
      () => ({
        ...company,
        address: {
          ...company.address,
          state: company.address?.state
            ? { value: company.address.state.id }
            : undefined,
          city: company.address?.city
            ? { value: company.address.city.id }
            : undefined,
        },
      }),
      [company],
    ),
    resolver: zodResolver(fullCompanySchema),
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const { control, formState } = form;

  useFormCepAutocomplete(form, 'address');

  const isLoading = formState.isSubmitting;
  const isDirty = formState.isDirty;

  const onReset = useCallback(() => form.reset(), [form]);

  const updateCompany = useUpdateCompany();

  const handleSubmit = useMemo(
    () =>
      form.handleSubmit(
        (data) =>
          updateCompany.mutateAsync({
            id: company.id,
            data: {
              ...data,
              address: {
                ...data.address,
                cityId: data.address.city?.value,
              },
            },
          }),

        (error) => {
          console.error('Erro ao atualizar empresa:', error);
        },
      ),
    [form, updateCompany, company.id],
  );

  return (
    <Form {...form}>
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <CardTitle>Visão Geral</CardTitle>
          <CardDescription>Informações sobre a empresa.</CardDescription>
        </CardHeader>

        <CardContent className="flex-1">
          <FormField
            control={control}
            name="tradingName"
            render={({ field }) => (
              <InlineInput
                title="Nome Fantasia"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          <FormField
            control={control}
            name="registrationNumber"
            render={({ field }) => (
              <InlineInput title="CNPJ" disabled={isLoading} {...field} />
            )}
          />
          <FormField
            control={control}
            name="companyName"
            render={({ field }) => (
              <InlineInput
                title="Razão Social"
                disabled={isLoading}
                {...field}
              />
            )}
          />

          <h3 className="text-lg font-semibold text-gray-800">Responsável</h3>
          <FormField
            control={control}
            name="responsibleName"
            render={({ field }) => (
              <InlineInput
                title="Nome do Responsável"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          <FormField
            control={control}
            name="responsibleEmail"
            render={({ field }) => (
              <InlineInput
                title="E-mail de Contato"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          <FormField
            control={control}
            name="responsiblePhone"
            render={({ field }) => (
              <InlineInput
                title="Telefone de Contato"
                as={MaskedInput}
                maskOptions={{
                  mask: '(00) 00000-0000',
                }}
                disabled={isLoading}
                {...field}
              />
            )}
          />

          <h3 className="text-lg font-semibold text-gray-800">Endereço</h3>

          <FormField
            control={control}
            name="address.zipCode"
            render={({ field }) => (
              <InlineInput
                title="CEP"
                as={MaskedInput}
                maskOptions={{
                  mask: '00000-000',
                }}
                disabled={isLoading}
                {...field}
              />
            )}
          />

          <FormField
            control={control}
            name="address.state"
            render={({ field: { ref: _, ...stateField } }) => (
              <FormField
                control={control}
                name="address.city"
                render={({ field: { ref: __, ...cityField } }) => (
                  <>
                    <InlineInput
                      className="w-full"
                      title="Estado"
                      as={UfSelector}
                      disabled={isLoading}
                      {...stateField}
                      onChange={(option) => {
                        stateField.onChange(option);
                        cityField.onChange(undefined);
                      }}
                    />

                    <InlineInput
                      className="w-full"
                      title="Cidade"
                      as={CitySelector}
                      disabled={isLoading}
                      stateId={stateField.value?.value}
                      {...cityField}
                    />
                  </>
                )}
              />
            )}
          />

          <FormField
            control={control}
            name="address.neighborhood"
            render={({ field: { ref: _, ...field } }) => (
              <InlineInput title="Bairro" disabled={isLoading} {...field} />
            )}
          />
          <FormField
            control={control}
            name="address.street"
            render={({ field: { ref: _, ...field } }) => (
              <InlineInput title="Rua" disabled={isLoading} {...field} />
            )}
          />
          <FormField
            control={control}
            name="address.number"
            render={({ field }) => (
              <InlineInput title="Número" disabled={isLoading} {...field} />
            )}
          />
          <FormField
            control={control}
            name="address.complement"
            render={({ field }) => (
              <InlineInput
                title="Complemento"
                disabled={isLoading}
                {...field}
              />
            )}
          />
        </CardContent>
        <CardFooter className="flex justify-end border-t px-6 py-4 gap-4">
          <Button variant="outline" disabled={!isDirty} onClick={onReset}>
            Limpar
          </Button>

          <Button
            type="submit"
            disabled={isLoading || !isDirty}
            loading={isLoading}
            onClick={handleSubmit}
          >
            Salvar
          </Button>
        </CardFooter>
      </Card>
    </Form>
  );
};

export default CompanyOverview;
